import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import 'react-calendar-timeline/lib/Timeline.css';
import { GridStack, GridStackOptions, GridStackWidget } from 'gridstack';
import '../../../styles/gridstack.css';


import { centralAPI } from '../../../services/central-api';
import { MosaicCamera, MosaicPreset, cameraService } from '../../../services/central-api/cameras';
import VideoPlayer from '../../../components/VideoPlayer';
import { useAuth } from '../../../context/Auth';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import TouchableOpacityWithMiddleClick from '../../../components/TouchableOpacityWithMiddleClick';
import Toast from 'react-native-toast-message';
import Container from '../../../components/Container';
import { Hoverable } from 'react-native-web-hover';
import { WindowInformation } from '../../../services/window-information';
import { useQuery } from '@tanstack/react-query';
import { CamerasParamList } from '../../../typings/Params';
import { presetPositions } from './preset-positions';
import { translate } from '../../../services/translate';

interface CameraParams {
    watermark: string;
    mosaic: Mosaic;
    camera?: MosaicCamera;
}

function Camera({ watermark, mosaic, camera }: CameraParams) {
    const [videoPlayer, setVideoPlayer] = useState<VideoPlayer>();
    const videoPlayerRef = React.useCallback(async (videoPlayer: VideoPlayer) => {
        setVideoPlayer(videoPlayer);
    }, []);
    const isFocused = useIsFocused();

    useEffect(() => {
        (async () => {
            try {
                if (videoPlayer?.props?.camera?.streamUrl) {
                    // TODO: maybe i dont need to do that for each camera??
                    const token = await centralAPI.getToken();
                    videoPlayer.setSrc(videoPlayer.props.camera.streamUrl, { Authorization: `Bearer ${token}` });
                }
            } catch (err) {
                console.error(err);
            }
        })();
    }, [videoPlayer]);

    useEffect(() => {
        if (!videoPlayer) {
            return;
        }

        if (!isFocused) {
            videoPlayer.destroyPlayers();
        }
    }, [videoPlayer, isFocused]);

    if (!camera) {
        return <></>;
    }

    return <div style={{ display: 'flex', flex: 1 }}>
        {mosaic.displayCameraTitle && (
            <View style={{ position: 'absolute', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000, padding: 5, borderRadius: 5 }}>
                <MyAppText style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>{camera.title}</MyAppText>
            </View>
        )}
        <VideoPlayer
            camera={camera}
            ref={videoPlayerRef}
            watermark={watermark}
            enableCameraInfo={false}
            enableZoom={false}
        />
    </div>;
}


export default function MosaicDetail() {
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CamerasParamList, 'MosaicDetail'>>();
    const { theme } = useStyles(stylesheet);

    const gridRef = useRef<HTMLDivElement | null>(null);
    const [grid, setGrid] = useState<GridStack>();
    const [height, setHeight] = useState(0);
    const [selectedCard, setSelectedCard] = useState<string | null>(null);
    const [renderedCards, setRenderedCards] = useState(false);
    // const [isModalVisible, setIsModalVisible] = useState(false);
    // const [deleteMode, setDeleteMode] = useState(false);
    const [dragging, setDragging] = useState(false);

    const windowInfo = WindowInformation();

    interface VideoSquare {
        x: number;
        y: number;
        w: number;
        h: number;
        minW: number;
        minH: number;
        camera?: MosaicCamera;
        position: string;
    }

    const [videos, setVideos] = useState<VideoSquare[]>([]);

    function rearrangePositions(cameras: MosaicCamera[], preset: number) {
        const used = new Set<number>();
        const wrongValues: MosaicCamera[] = [];

        for (const cam of cameras) {
            const currentPos = cam.position;
            if (currentPos <= preset && !used.has(currentPos)) {
                used.add(currentPos);
                continue;
            }
            wrongValues.push(cam);
        }

        for (const wrongValue of wrongValues) {
            let newPos = 1;
            while (used.has(newPos) && newPos <= preset) {
                newPos++;
            }
            if (newPos <= preset) {
                wrongValue.position = newPos;
                used.add(newPos);
            }
        }
    }

    const isFocused = useIsFocused();
    const { data } = useQuery({
        queryKey: ['getMosaic', route.params.id],
        queryFn: () => Promise.all([
            cameraService.getMosaic(route.params.id),
            cameraService.getMosaicCameras(route.params.id),
        ]).then((data) => {
            const [mosaic, cameras] = data;

            const mosaicPreset = mosaic.preset as MosaicPreset;
            rearrangePositions(cameras, Number(mosaicPreset));

            const preset = presetPositions[mosaicPreset];
            setVideos(preset.map((video, index) => {
                const camera = cameras.find(c => c.position === index + 1);
                return { ...video, camera, position: String(index + 1) };
            }));


            return {
                mosaic,
                cameras,
            };
        }),
        enabled: isFocused && Boolean(authenticatedUser) && Boolean(route.params?.id),
    });

    useQuery({
        queryKey: ['checkAuthentication'],
        queryFn: () => centralAPI.checkAuthentication(),
        enabled: isFocused && Boolean(authenticatedUser) && Boolean(route.params?.id),
        refetchInterval: 30000,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!isFocused) {
            setRenderedCards(false);
            return;
        }

        if (!gridRef.current) {
            return;
        }

        if (grid) {
            // FIXME: this is crap but i couldn't figure out
            // how to proper clean/"destroy" this old/trash grid
            // without exploding the whole page.
            window.location.href = `/cameras/mosaic/${route.params.id}`;
            return;
        }

        const myGrid = GridStack.init(
            {
                auto: false,
                cellHeight: 75,
                maxRow: 12,
                // this may be useful if using mobile
                disableOneColumnMode: true,
                margin: 1,
                float: true,
                draggable: {
                    handle: '.drag-handle',
                },
                // TODO: ?
                // resizable: {
                //     handles: 'all',
                // },
                disableResize: true,
            },
            gridRef.current
        );

        setGrid(myGrid);
    }, [isFocused]);

    async function saveChangedPositions(layout: GridStackOptions | GridStackWidget[]) {

        if (Array.isArray(layout)) {

            const positions = [];

            for (const [index, item] of layout.entries()) {
                const videoSquare = item as VideoSquare;
                if (videoSquare.camera) {
                    positions.push({
                        cameraId: videoSquare.camera.id,
                        position: index + 1,
                    });
                }
            }

            await cameraService.changeMosaicCamerasPositions(route.params.id, { positions });
        }
    }

    useEffect(() => {
        if (!grid) {
            return;
        }

        grid.on('dragstart', () => {
            setDragging(true);
        });

        grid.on('dragstop', () => {
            setDragging(false);
            const layout = grid.save(false);
            saveChangedPositions(layout);
        });

        grid.on('change', () => {
            const layout = grid.save(false);
            if (Array.isArray(layout)) {
                for (const item of layout) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    const video = videos.find(video => item.position == video.position);
                    if (video) {
                        if (item.x) {
                            video.x = item.x;
                        }
                        if (item.y) {
                            video.y = item.y;
                        }
                        if (item.w) {
                            video.w = item.w;
                        }
                        if (item.h) {
                            video.h = item.h;
                        }
                    }
                }
            }
        });

        return () => {
            grid.off('change');
        };

    }, [grid, videos]);

    useEffect(() => {
        if (!grid || !height) {
            return;
        }
        grid.cellHeight(height / 12, true);
    }, [grid, height]);

    useEffect(() => {
        if (!grid || !renderedCards) {
            return;
        }

        for (const video of videos) {
            const el = grid.getGridItems().find((el) => el.getAttribute('data-gs-id') === video.position);

            if (!el) {
                continue;
            }

            if (!el.getAttribute('gs-x')) {
                grid.makeWidget(el);
            }

            grid.update(el, video);
        }

    }, [grid, renderedCards, videos]);

    // TODO:
    // function deleteVideo(grid: GridStack, cameraId: string) {

    //     const video = videos.find(video => video.cameraId === cameraId);
    //     if (video) {
    //         const el = grid.getGridItems().find((el) => el.getAttribute('data-gs-id') === video.cameraId);
    //         if (el) {
    //             grid.removeWidget(el, false);
    //         }
    //     }
    //     setVideos(videos.filter(video => video.cameraId !== cameraId));
    //     setDeleteMode(false);
    // }

    function swapPlaces(grid: GridStack, position: string) {
        const el1 = grid.getGridItems().find((el) => el.getAttribute('data-gs-id') === position);
        const el2 = grid.getGridItems().find((el) => el.getAttribute('data-gs-id') === selectedCard);

        if (!el1 || !el2) {
            return;
        }

        const el1Values = {
            x: Number(el1.getAttribute('gs-x')),
            y: Number(el1.getAttribute('gs-y')),
            w: Number(el1.getAttribute('gs-w')),
            h: Number(el1.getAttribute('gs-h')),
        };

        const el2Values = {
            x: Number(el2.getAttribute('gs-x')),
            y: Number(el2.getAttribute('gs-y')),
            w: Number(el2.getAttribute('gs-w')),
            h: Number(el2.getAttribute('gs-h')),
        };

        grid.batchUpdate();
        grid.update(el1, el2Values);
        grid.update(el2, el1Values);
        grid.commit();
        setSelectedCard(null);

        grid.update(el1, el2Values);
        grid.update(el2, el1Values);

        const layout = grid.save(false);
        saveChangedPositions(layout);

    }

    function clickedItem(position: string) {
        if (!grid) {
            return;
        }

        // TODO:
        // if (deleteMode) {
        //     deleteVideo(grid, cameraId);
        //     return;
        // }

        if (!selectedCard) {
            setSelectedCard(position);

            if (windowInfo.isMobile) {
                Toast.show({
                    type: 'sentinelInfo',
                    text1: translate('clickOnAnotherCameraToChangePosition'),
                });
            }
        }
        if (selectedCard === position) {
            setSelectedCard(null);
        }

        swapPlaces(grid, position);
    }

    // TODO:
    // function createItem() {
    //     setIsModalVisible(false);

    //     const spot = findFreeSpot(
    //         videos,
    //         { w: 2, h: 2 },
    //     );

    //     if (!spot) {
    //         Toast.show({
    //             type: 'sentinelxWarning',
    //             text1: 'não tem mais espaco',
    //         });
    //         return;
    //     }


    //     setVideos([...videos, {
    //         url: 'http://localhost:8000/1763542151438_1763542284714.mp4', cameraId: 'camera-' + Math.floor(Math.random() * 1000), x: spot.x, y: spot.y, w: 2, h: 2, minW: 2, minH: 2,
    //     }]);
    // }

    // TODO:
    // function onDeleteMode() {
    //     setIsModalVisible(false);
    //     setSelectedCard(null);
    //     setDeleteMode(!deleteMode);
    // }

    // TODO:
    // const isFocused = useIsFocused();
    // useFocusEffect(useCallback(() => {
    //     const handler = (e: KeyboardEvent) => {
    //         if (e.code === 'Space') {
    //             e.preventDefault(); // evita scroll da página
    //             setIsModalVisible(!isModalVisible);
    //         }
    //         if (e.code === 'KeyE') {
    //             e.preventDefault(); // evita scroll da página
    //             onDeleteMode();
    //         }
    //         if (e.code === 'KeyC') {
    //             e.preventDefault(); // evita scroll da página
    //             createItem();
    //         }
    //     };
    //     if (isFocused) {
    //         window.addEventListener('keydown', handler);
    //     }
    //     return () => window.removeEventListener('keydown', handler);

    // }, [isFocused, videos, deleteMode, isModalVisible]));


    return (
        <Container style={{ flex: 1 }} onLayout={(event) => {
            const { height } = event.nativeEvent.layout;
            setHeight(height);
        }}>
            <div ref={gridRef} className="grid-stack" style={{ flex: 1, backgroundColor: '#E2E2E2' }}>
                {videos.map((video) => (
                    <div
                        key={video.position}
                        className="grid-stack-item"
                        data-gs-id={video.position}
                        data-gs-x={video.x}
                        data-gs-y={video.y}
                        data-gs-w={video.w}
                        data-gs-h={video.h}

                    >
                        <div className="grid-stack-item-content camera-card drag-handle" style={{
                            overflow: 'hidden',
                            textAlign: 'center',
                            backgroundColor: '#000',
                            borderRadius: dragging ? 8 : 0,
                        }}>
                            <TouchableOpacityWithMiddleClick
                                onLayout={() => setRenderedCards(true)}
                                style={[{
                                    width: '100%',
                                    height: '100%',
                                }]}
                                onPress={() => clickedItem(video.position)}
                                onMiddleClick={() => {
                                    if (video.camera) {
                                        window.open(`/cameras/map/${video.camera.id}`);
                                    }
                                }}
                            >
                                <Hoverable style={{
                                    width: '100%',
                                    height: '100%',
                                }}>
                                    {() => (
                                        <>
                                            {data && authenticatedUser && video.camera &&
                                                <Camera
                                                    mosaic={data.mosaic}
                                                    camera={video.camera}
                                                    watermark={String(authenticatedUser.id)}
                                                />
                                            }
                                            {video.position === selectedCard && <div style={{
                                                position: 'absolute',
                                                inset: 0,
                                                backdropFilter: 'blur(6px)',
                                                background: theme.colors.primary['semi-transparent'],
                                                pointerEvents: 'none',
                                                alignContent: 'center',
                                            }}>
                                                <MyAppText style={{
                                                    color: '#fafafa',

                                                }}>{!windowInfo.isMobile ? translate('clickOnAnotherCameraToChangePosition') : ''}</MyAppText>
                                            </div>}
                                            {/* TODO: */}
                                            {/* {deleteMode && hovered && <div style={{
                                                position: 'absolute',
                                                inset: 0,
                                                backdropFilter: 'blur(6px)',
                                                background: '#ff000064',
                                                pointerEvents: 'none',
                                                alignContent: 'center',
                                            }}>
                                                <MyAppText style={{ color: '#fafafa' }}>{'Remover câmera'}</MyAppText>
                                            </div>} */}
                                        </>
                                    )}
                                </Hoverable>

                            </TouchableOpacityWithMiddleClick>
                        </div>

                    </div>
                ))}
            </div>
            {/* TODO: */}
            {/* <Modal
                visible={isModalVisible}
                animationType='fade'
                onRequestClose={() => {
                    setIsModalVisible(false);
                }}
                transparent={true}
            >
                <View style={{
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#31313199',
                    flex: 1
                }}>
                    <View style={{
                        minWidth: 300,
                        backgroundColor: '#fff',
                        flexDirection: 'column',
                        borderRadius: 4,
                        padding: 25,
                        rowGap: 20
                    }}>
                        <CustomButton
                            type='primary'
                            text={'(C)riar'}
                            onPress={() => {
                                createItem();
                            }}

                        />
                        <CustomButton
                            type='destructive'
                            text={'(E)xcluir'}
                            onPress={() => {
                                onDeleteMode();
                            }}

                        />
                    </View>
                </View>
            </Modal> */}
        </Container>
    );

}

const stylesheet = createStyleSheet(() => ({}));
